import { useCallback, useRef, useState } from 'react';

interface UseDragAndDropOptions {
    onDrop: (files: File[]) => void;
    disabled?: boolean;
}

export const useDragAndDrop = ({ onDrop, disabled = false }: UseDragAndDropOptions) => {
    const [isDragActive, setIsDragActive] = useState(false);
    const dragCounter = useRef(0);

    const handleDragEnter = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            dragCounter.current += 1;
            if (!disabled && dragCounter.current === 1) {
                setIsDragActive(true);
            }
        },
        [disabled]
    );

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current -= 1;
        if (dragCounter.current === 0) {
            setIsDragActive(false);
        }
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            dragCounter.current = 0;
            setIsDragActive(false);

            if (disabled) {
                return;
            }

            const files = Array.from(e.dataTransfer.files);
            if (files.length > 0) {
                onDrop(files);
            }
        },
        [disabled, onDrop]
    );

    const dragProps = {
        onDragEnter: handleDragEnter,
        onDragOver: handleDragOver,
        onDragLeave: handleDragLeave,
        onDrop: handleDrop,
    };

    return { isDragActive, dragProps };
};